/**
 * Storage adapter for React Query cache persistence.
 *
 * Backed by `localStorage` on every platform. It works inside the Capacitor
 * WebView and persists across app restarts for the app's fixed origin.
 *
 * We do NOT use `@capacitor/preferences`: in the Android WebView its native
 * calls never resolve, so `get` hung and the cache silently never hydrated,
 * while `set`/`remove` were no-ops. The same hang wedged supabase-js's session
 * handling — see the note in `lib/supabase.ts`.
 */

// Include API URL in key so local/staging/prod don't share cached data
const ENV_SUFFIX = (import.meta.env.VITE_API_URL || "local").replace(
  /[^a-zA-Z0-9]/g,
  "_",
);
const STORAGE_KEY = `tinyapps-query-cache-${ENV_SUFFIX}`;

/** Synchronous storage interface for `createSyncStoragePersister`. */
export const queryStorage = {
  getItem: (key: string): string | null => {
    if (key !== STORAGE_KEY) return null;
    return localStorage.getItem(STORAGE_KEY);
  },
  setItem: (key: string, value: string): void => {
    if (key !== STORAGE_KEY) return;
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Quota exceeded — drop the stale cache rather than wedging writes.
      localStorage.removeItem(STORAGE_KEY);
    }
  },
  removeItem: (key: string): void => {
    if (key !== STORAGE_KEY) return;
    localStorage.removeItem(STORAGE_KEY);
  },
};

export { STORAGE_KEY };
